import React, { useEffect } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import router from './router';
import queryString from 'query-string';
import { createPath } from 'history';
import history from './core/history';
import App from './components/App';
import configureStore from './store/configureStore';
import { updateMeta } from './core/DOMUtils';
import { ErrorReporter, deepForceUpdate } from './core/devUtils';
import createApolloClient from './core/createApolloClient';
import { getIntl } from './actions/intl';
const apolloClient = createApolloClient();

const store = configureStore(window.APP_STATE, { history, apolloClient });
// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html


const insertCss = (...styles) => {
  // eslint-disable-next-line no-underscore-dangle
  const removeCss = styles.map(x => x._insertCss());
  return () => {
    removeCss.forEach(f => f());
  };
};

const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  // insertCss: (...styles) => {
  //   // eslint-disable-next-line no-underscore-dangle
  //   const removeCss = styles.map(x => x._insertCss());
  //   return () => { removeCss.forEach(f => f()); };
  // },
  // For react-apollo
  client: apolloClient,
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store,
  intl: store.dispatch(getIntl()),
};

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {};
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

/*let onRenderComplete = function initialRenderComplete() {
  const elem = document.getElementById('css');
  if (elem) elem.parentNode.removeChild(elem);
  onRenderComplete = function renderComplete(route, location) {
    document.title = route.title;

    updateMeta('description', route.description);
    // Update necessary tags in <head> at runtime here, ie:onRenderComplete
    // updateMeta('keywords', route.keywords);
    // updateCustomMeta('og:url', route.canonicalUrl);
    // updateCustomMeta('og:image', route.imageUrl);
    // updateLink('canonical', route.canonicalUrl);
    // etc.

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY);

    // Google Analytics tracking. Don't send 'pageview' event after
    // the initial rendering, as it was already sent
    if (window.ga) {
      window.ga('send', 'pageview', createPath(location));
    }
  };
};*/

// Make taps on links and buttons work fast on mobiles
// FastClick.attach(document.body);

const container = document.getElementById('app');
let appInstance, reactRoot, reactHydrate;
let currentLocation = history.location;
let routes = require('./routes').default;

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;
  const isInitialRender = !action;

  try {
    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    let locale = store.getState().intl.locale;
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);
    context.locale = locale;
    const route = await router.resolve(context);


    // CheckForTheme
    let theme = store.getState().currency.theme;
    if (__DEV__) {
      if (theme == 'dark') {
        document.body.classList.add('darkMode');
        document.body.classList.remove('lightMode');
      } else {
        document.body.classList.add('lightMode');
        document.body.classList.remove('darkMode');
      }
    }


    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    const AppCallback = () => {
      useEffect(() => {
        const elem = document.getElementById('css');
        if (elem) elem.parentNode.removeChild(elem);
        return;
      })
      return <App locale={locale} context={context} insertCss={insertCss}>{route.component}</App>;
    }

    if (isInitialRender) {
      appInstance = hydrateRoot(container, <AppCallback />)
      reactHydrate = appInstance;
    } else {
      appInstance = reactHydrate?.render(<App locale={locale} context={context} insertCss={insertCss}>{route.component}</App>);
    }

    // Only update the document title if this tab is focused/active
    // This prevents tabs from affecting each other's titles
    if (document.hasFocus()) {
      document.title = route.title;
      updateMeta('description', route.description);
    }

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY);
    // Google Analytics tracking. Don't send 'pageview' event after
    // the initial rendering, as it was already sent
    if (window.ga) {
      window.ga('send', 'pageview', createPath(location));
    }

  } catch (error) {
    // Display the error in full-screen for development mode
    if (__DEV__) {
      appInstance = null;
      document.title = `Error: ${error.message}`;
      reactRoot = !reactRoot ? createRoot(container) : reactRoot;
      reactRoot.render(<ErrorReporter error={error} />);
      throw error;
    }

    console.error(error); // eslint-disable-line no-console

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
// history.listen(onLocationChange);
// onLocationChange(currentLocation);

export default function main() {
  // Handle client-side navigation by using HTML5 History API
  // For more information visit https://github.com/mjackson/history#readme
  currentLocation = history.location;
  history.listen(({ location, action }) => onLocationChange(location, action));
  onLocationChange(currentLocation);
  
  // Listen for logout events from other tabs
  if (typeof window !== 'undefined') {
    // Initial check for special routes on page load
    checkAndRedirectSpecialRoutes();
    
    // Set interval to periodically check authentication for special routes
    setInterval(checkAndRedirectSpecialRoutes, 2000);
    
    // Listen for storage events (logout in other tabs)
    window.addEventListener('storage', (event) => {
      // Check for any logout indicators
      const isLogoutEvent = [
        'isAuthenticated', 
        'isAdminAuthenticated',
        'logout_timestamp', 
        'admin_global_logout',
        'force_logout_special_routes'
      ].includes(event.key);
      
      if (isLogoutEvent) {
        checkAndRedirectSpecialRoutes();
      }
    });
  }
}

// Function to check if current page is a special route and redirect if needed
function checkAndRedirectSpecialRoutes() {
  if (typeof window === 'undefined') return;

  try {
    // Get current path
    
  } catch (error) {
    console.error('Error checking authentication status:', error);
  }
}

// Handle errors that might happen after rendering
// Display the error in full-screen for development mode
if (__DEV__) {
  window.addEventListener('error', (event) => {
    appInstance = null;
    document.title = `Runtime Error: ${event.error.message}`;
    reactRoot = !reactRoot ? createRoot(container) : reactRoot;
    reactRoot.render(<ErrorReporter error={event.error} />);
  });
}

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./routes', async () => {
    routes = require('./routes').default; // eslint-disable-line global-require

    currentLocation = history.location;


    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    await onLocationChange(currentLocation);
  });
}
